import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adjustStock,
  createWarehouse,
  listLowStock,
  listWarehouses,
  transferStock,
  updateWarehouse,
} from './warehouses.api';
import { listProducts } from '../products/products.api';
import { hasPermission, useMe } from '../../hooks/useAuth';
import { readApiError } from '../../lib/api';

export function WarehousesPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, 'inventory.write');
  const qc = useQueryClient();

  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: listWarehouses });
  const lowStock = useQuery({ queryKey: ['low-stock'], queryFn: listLowStock });
  const products = useQuery({
    queryKey: ['products', { limit: 200 }],
    queryFn: () => listProducts({ limit: 100 }),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['warehouses'] });
    void qc.invalidateQueries({ queryKey: ['products'] });
    void qc.invalidateQueries({ queryKey: ['low-stock'] });
  };

  const createMut = useMutation({
    mutationFn: createWarehouse,
    onSuccess: () => {
      setShowCreate(false);
      invalidateAll();
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const transferMut = useMutation({
    mutationFn: transferStock,
    onSuccess: () => {
      setShowTransfer(false);
      invalidateAll();
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const adjustMut = useMutation({
    mutationFn: adjustStock,
    onSuccess: invalidateAll,
    onError: (err) => setError(readApiError(err).message),
  });

  const toggleDefault = useMutation({
    mutationFn: (wh: { id: number; isDefault: boolean }) =>
      updateWarehouse(wh.id, { isDefault: wh.isDefault }),
    onSuccess: invalidateAll,
  });

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Bodegas</h1>
        {canWrite && (
          <>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + Nueva bodega
            </button>
            <button className="btn" onClick={() => setShowTransfer(true)}>
              Transferir stock
            </button>
          </>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {warehouses.data && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Código</th>
                <th style={{ padding: '0.75rem 1rem' }}>Nombre</th>
                <th style={{ padding: '0.75rem 1rem' }}>Dirección</th>
                <th style={{ padding: '0.75rem 1rem' }}>Default</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.data.map((w) => (
                <tr key={w.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem 1rem' }}><code>{w.code}</code></td>
                  <td style={{ padding: '0.75rem 1rem' }}>{w.name}</td>
                  <td style={{ padding: '0.75rem 1rem', color: 'var(--color-muted)' }}>{w.address ?? '-'}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {canWrite ? (
                      <input
                        type="checkbox"
                        checked={w.isDefault}
                        onChange={() => toggleDefault.mutate({ id: w.id, isDefault: !w.isDefault })}
                      />
                    ) : (
                      w.isDefault ? '✓' : ''
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lowStock.data && lowStock.data.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem', borderColor: 'var(--color-danger)' }}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Stock por debajo del mínimo</h3>
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            {lowStock.data.map((row) => {
              const product = products.data?.data.find((p) => p.id === row.productId);
              const wh = warehouses.data?.find((w) => w.id === row.warehouseId);
              return (
                <li key={`${row.productId}-${row.warehouseId}`}>
                  {product?.name ?? `Producto ${row.productId}`} en {wh?.name ?? `Bodega ${row.warehouseId}`}: {row.quantity} (mín {row.minStock})
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showCreate && (
        <CreateModal
          onCancel={() => setShowCreate(false)}
          onSubmit={(body) => createMut.mutate(body)}
          submitting={createMut.isPending}
        />
      )}
      {showTransfer && warehouses.data && products.data && (
        <TransferModal
          warehouses={warehouses.data}
          products={products.data.data.filter((p) => !p.isService)}
          onCancel={() => setShowTransfer(false)}
          onSubmit={(body) => transferMut.mutate(body)}
          submitting={transferMut.isPending}
        />
      )}

      {canWrite && warehouses.data && products.data && (
        <details className="card" style={{ marginTop: '1rem' }}>
          <summary style={{ cursor: 'pointer' }}>Ajustar stock manualmente</summary>
          <AdjustForm
            warehouses={warehouses.data}
            products={products.data.data.filter((p) => !p.isService)}
            onSubmit={(b) => adjustMut.mutate(b)}
            submitting={adjustMut.isPending}
          />
        </details>
      )}
    </section>
  );
}

function CreateModal(props: {
  onCancel: () => void;
  onSubmit: (b: { code: string; name: string; address?: string; isDefault?: boolean }) => void;
  submitting: boolean;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 420 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({ code, name, address: address || undefined, isDefault });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Nueva bodega</h3>
        <div className="field">
          <label>Código</label>
          <input className="input" value={code} onChange={(e) => setCode(e.target.value)} required />
        </div>
        <div className="field">
          <label>Nombre</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label>Dirección (opcional)</label>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Bodega por defecto
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Guardando...' : 'Crear'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function TransferModal(props: {
  warehouses: { id: number; code: string; name: string }[];
  products: { id: number; name: string; sku: string }[];
  onCancel: () => void;
  onSubmit: (b: { productId: number; fromWarehouseId: number; toWarehouseId: number; quantity: number; note?: string }) => void;
  submitting: boolean;
}) {
  const [productId, setProductId] = useState(props.products[0]?.id ?? 0);
  const [from, setFrom] = useState(props.warehouses[0]?.id ?? 0);
  const [to, setTo] = useState(props.warehouses[1]?.id ?? props.warehouses[0]?.id ?? 0);
  const [qty, setQty] = useState('1');
  const [note, setNote] = useState('');

  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 480 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({
            productId,
            fromWarehouseId: from,
            toWarehouseId: to,
            quantity: Number(qty),
            note: note || undefined,
          });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Transferir stock</h3>
        <div className="field">
          <label>Producto</label>
          <select className="input" value={productId} onChange={(e) => setProductId(Number(e.target.value))}>
            {props.products.map((p) => (
              <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Origen</label>
            <select className="input" value={from} onChange={(e) => setFrom(Number(e.target.value))}>
              {props.warehouses.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Destino</label>
            <select className="input" value={to} onChange={(e) => setTo(Number(e.target.value))}>
              {props.warehouses.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
            </select>
          </div>
        </div>
        <div className="field">
          <label>Cantidad</label>
          <input className="input" type="number" min="0.001" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} required />
        </div>
        <div className="field">
          <label>Nota (opcional)</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Procesando...' : 'Transferir'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function AdjustForm(props: {
  warehouses: { id: number; code: string }[];
  products: { id: number; sku: string; name: string }[];
  onSubmit: (b: { productId: number; warehouseId: number; delta: number; note?: string }) => void;
  submitting: boolean;
}) {
  const [productId, setProductId] = useState(props.products[0]?.id ?? 0);
  const [warehouseId, setWarehouseId] = useState(props.warehouses[0]?.id ?? 0);
  const [delta, setDelta] = useState('1');
  const [note, setNote] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit({ productId, warehouseId, delta: Number(delta), note: note || undefined });
      }}
      style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr auto', gap: '0.5rem', alignItems: 'end', marginTop: '0.75rem' }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>Producto</label>
        <select className="input" value={productId} onChange={(e) => setProductId(Number(e.target.value))}>
          {props.products.map((p) => (
            <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
          ))}
        </select>
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Bodega</label>
        <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(Number(e.target.value))}>
          {props.warehouses.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
        </select>
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Δ Cantidad</label>
        <input className="input" type="number" step="0.001" value={delta} onChange={(e) => setDelta(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Nota</label>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <button type="submit" className="btn btn-primary" disabled={props.submitting}>
        Aplicar
      </button>
    </form>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};
